import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heading } from '../../components/heading';
import { Text } from '../../components/text';
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '../../components/table';
import { useUser } from '../../components/UserContext';
import { usePendientesDecorados } from '../../hooks/usePendientes';
import { useOrgMiembros } from '../../hooks/useOrgMiembros';
import {
  PENDIENTE_TIPOS,
  PENDIENTE_TIPO_LABELS,
  ORG_LABEL,
  contarPendientes,
  esDeLaOrg,
  estaAsignado,
  normalizarAsignados,
  ordenarPorUrgencia,
} from '../../utils/pendientes';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

const fmtFecha = (ms) => {
  if (!ms) return '—';
  const d = new Date(Number(ms));
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
};

const diasDesde = (ms, now) => (ms ? Math.floor((now - Number(ms)) / 86400000) : 0);

const URGENCIA_LABEL = {
  vencido: 'Vencido',
  urgente: 'Esta semana',
  proximo: 'Próximos 30 días',
  normal: 'Más adelante',
  sinFecha: 'Sin fecha',
};
const URGENCIA_STYLE = {
  vencido: 'bg-red-100 text-red-800',
  urgente: 'bg-amber-100 text-amber-800',
  proximo: 'bg-cian-digital/15 text-cian-digital',
  normal: 'bg-gray-100 text-gray-700',
  sinFecha: 'bg-gray-100 text-gray-500',
};

const GROUP_OPTIONS = [
  { key: 'fiado', label: 'Fiado' },
  { key: 'asignado', label: 'Asignado' },
  { key: 'tipo', label: 'Tipo' },
  { key: 'afianzadora', label: 'Afianzadora' },
];

/**
 * Reporte de pendientes: a snapshot of everything still open in the org's
 * `pendientes` collection, rolled up by fiado, asignado, tipo or
 * afianzadora, with the rows underneath. Endoso requests are NOT here —
 * they live on the fianza and are reported from Pulso.
 *
 * Same shape as Cobranza de primas: tiles, a grouping control, an
 * expandable table. "Ver mis asignados" is a personal lens over the same
 * org-wide rows, not an access boundary.
 */
export function ReportePendientes() {
  const navigate = useNavigate();
  const { user } = useUser();
  const uid = user?.uid || null;
  const { abiertos, loading } = usePendientesDecorados({ soloAbiertos: true });
  const { nombreDe } = useOrgMiembros();

  const [groupBy, setGroupBy] = useState('fiado');
  const [soloMios, setSoloMios] = useState(false);
  const [filtroUrgencia, setFiltroUrgencia] = useState(null); // vencido | urgente | null
  const [expanded, setExpanded] = useState(new Set());

  const now = Date.now();
  const misCount = useMemo(() => abiertos.filter((p) => estaAsignado(p, uid)).length, [abiertos, uid]);

  const rows = useMemo(() => {
    let out = abiertos;
    if (soloMios) out = out.filter((p) => estaAsignado(p, uid));
    if (filtroUrgencia) out = out.filter((p) => p.urgencia === filtroUrgencia);
    return out;
  }, [abiertos, soloMios, filtroUrgencia, uid]);

  const counts = useMemo(() => contarPendientes(soloMios ? abiertos.filter((p) => estaAsignado(p, uid)) : abiertos), [abiertos, soloMios, uid]);
  const compromisos = useMemo(
    () => abiertos.filter((p) => p.tipo === PENDIENTE_TIPOS.COMPROMISO_AFIANZADORA).length,
    [abiertos]
  );

  const keysOf = (p) => {
    switch (groupBy) {
      case 'asignado': {
        const lista = normalizarAsignados(p);
        return lista.length ? lista.map((u) => ({ key: `u:${u}`, label: u === uid ? 'Yo' : (nombreDe(u) || u) })) : [{ key: 'u:', label: 'Sin asignar' }];
      }
      case 'tipo':
        return [{ key: `t:${p.tipo}`, label: PENDIENTE_TIPO_LABELS[p.tipo] || p.tipo }];
      case 'afianzadora':
        return [{ key: `a:${p.afianzadora || ''}`, label: p.afianzadora || 'Sin afianzadora' }];
      default: {
        if (esDeLaOrg(p)) return [{ key: 'c:org', label: ORG_LABEL }];
        const id = p.convenioId || p.clienteId || '';
        return [{ key: `c:${id}`, label: `${p.clientName}${p.convenioId ? ' (convenio)' : ''}` }];
      }
    }
  };

  const grupos = useMemo(() => {
    const m = new Map();
    rows.forEach((p) => {
      keysOf(p).forEach(({ key, label }) => {
        if (!m.has(key)) m.set(key, { key, label, rows: [], vencidos: 0, urgentes: 0, masViejo: 0 });
        const g = m.get(key);
        g.rows.push(p);
        if (p.urgencia === 'vencido') g.vencidos += 1;
        if (p.urgencia === 'urgente') g.urgentes += 1;
        g.masViejo = Math.max(g.masViejo, diasDesde(p.createdAt, now));
      });
    });
    return [...m.values()]
      .map((g) => ({ ...g, rows: ordenarPorUrgencia([...g.rows]) }))
      .sort((a, b) => (b.vencidos - a.vencidos) || (b.urgentes - a.urgentes) || (b.rows.length - a.rows.length)
        || a.label.localeCompare(b.label, 'es'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, groupBy, nombreDe, now]);

  const toggle = (key) => setExpanded((s) => {
    const n = new Set(s);
    if (n.has(key)) n.delete(key); else n.add(key);
    return n;
  });

  const irAlRelacionado = (p) => {
    if (p.convenioId) return navigate(`/app/convenios/${p.convenioId}`);
    if (p.clienteId) return navigate(`/app/clientes/${p.clienteId}`);
    return navigate('/app/pendientes');
  };

  const antiguedad = (d) => (d <= 0 ? 'hoy' : `${d} d`);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-azul-surelia"></div>
      </div>
    );
  }

  const groupLabel = GROUP_OPTIONS.find((o) => o.key === groupBy)?.label || '';

  return (
    <div>
      <div className="mb-4">
        <Heading level={2} className="text-xl text-azul-surelia">Pendientes</Heading>
        <Text className="text-sm text-azul-surelia/70">
          Lo que sigue abierto al día de hoy{soloMios ? ', solo tus asignados' : ''}
        </Text>
      </div>

      {/* Same strip as Beneficiarios: compact totals on the left, the two
          controls (agrupar, mis asignados) on the right. Vencidos and Esta
          semana double as filters, like the riesgo tile in Cobranza. */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="rounded-lg border border-azul-surelia/20 bg-white p-3 min-w-[120px]">
          <Text className="text-xs text-azul-surelia/70">Abiertos</Text>
          <Text className="text-xl font-bold text-azul-surelia tabular-nums">{counts.total}</Text>
        </div>
        <button
          type="button"
          onClick={() => setFiltroUrgencia(filtroUrgencia === 'vencido' ? null : 'vencido')}
          className={`rounded-lg border p-3 min-w-[120px] text-left transition-shadow hover:shadow-sm ${
            filtroUrgencia === 'vencido' ? 'ring-2 ring-azul-surelia/40' : ''
          } ${counts.vencidos > 0 ? 'bg-red-50 border-red-200' : 'bg-white border-azul-surelia/20'}`}
        >
          <Text className="text-xs text-azul-surelia/70">Vencidos</Text>
          <Text className={`text-xl font-bold tabular-nums ${counts.vencidos > 0 ? 'text-red-700' : 'text-azul-surelia'}`}>{counts.vencidos}</Text>
        </button>
        <button
          type="button"
          onClick={() => setFiltroUrgencia(filtroUrgencia === 'urgente' ? null : 'urgente')}
          className={`rounded-lg border p-3 min-w-[120px] text-left transition-shadow hover:shadow-sm ${
            filtroUrgencia === 'urgente' ? 'ring-2 ring-azul-surelia/40' : ''
          } ${counts.urgentes > 0 ? 'bg-amber-50 border-amber-200' : 'bg-white border-azul-surelia/20'}`}
        >
          <Text className="text-xs text-azul-surelia/70">Esta semana</Text>
          <Text className={`text-xl font-bold tabular-nums ${counts.urgentes > 0 ? 'text-amber-700' : 'text-azul-surelia'}`}>{counts.urgentes}</Text>
        </button>
        <div className="rounded-lg border border-azul-surelia/20 bg-white p-3 min-w-[120px]">
          <Text className="text-xs text-azul-surelia/70">Compromisos</Text>
          <Text className="text-xl font-bold text-azul-surelia tabular-nums">{compromisos}</Text>
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg border border-azul-surelia/20 bg-white p-0.5">
            {GROUP_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => { setGroupBy(opt.key); setExpanded(new Set()); }}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  groupBy === opt.key
                    ? 'bg-azul-surelia text-white'
                    : 'text-azul-surelia hover:bg-azul-surelia/5'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {misCount > 0 && (
            <button
              type="button"
              onClick={() => { setSoloMios((v) => !v); setExpanded(new Set()); }}
              className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                soloMios
                  ? 'bg-azul-surelia text-white border-azul-surelia'
                  : 'bg-white text-azul-surelia border-azul-surelia/30 hover:bg-azul-surelia/5'
              }`}
            >
              {soloMios ? 'Toda la cartera' : `Mis asignados (${misCount})`}
            </button>
          )}
        </div>
      </div>

      {grupos.length === 0 ? (
        <div className="bg-white rounded-lg border border-azul-surelia/20 p-8 text-center">
          <Text className="text-sm text-zinc-500">Sin pendientes abiertos.</Text>
        </div>
      ) : (
        <div className="overflow-x-auto border border-azul-surelia/15 rounded-lg">
          <Table>
            <TableHead className="bg-azul-surelia text-white">
              <TableRow>
                <TableHeader className="text-white w-8"></TableHeader>
                <TableHeader className="text-white">{groupLabel}</TableHeader>
                <TableHeader className="text-white text-right">Abiertos</TableHeader>
                <TableHeader className="text-white text-right">Vencidos</TableHeader>
                <TableHeader className="text-white text-right">Esta semana</TableHeader>
                <TableHeader className="text-white text-right">Antigüedad</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {grupos.map((g) => {
                const open = expanded.has(g.key);
                return (
                  <React.Fragment key={g.key}>
                    <TableRow className="cursor-pointer hover:bg-azul-surelia/5" onClick={() => toggle(g.key)}>
                      <TableCell className="py-3 pr-0">
                        {open
                          ? <ChevronDownIcon className="w-4 h-4 text-zinc-400" />
                          : <ChevronRightIcon className="w-4 h-4 text-zinc-400" />}
                      </TableCell>
                      <TableCell className="py-3">
                        <Text className="text-sm font-medium text-azul-surelia">{g.label}</Text>
                      </TableCell>
                      <TableCell className="py-3 text-right">
                        <Text className="text-sm text-zinc-700 tabular-nums">{g.rows.length}</Text>
                      </TableCell>
                      <TableCell className="py-3 text-right">
                        <Text className={`text-sm tabular-nums ${g.vencidos > 0 ? 'text-red-700 font-semibold' : 'text-zinc-400'}`}>{g.vencidos}</Text>
                      </TableCell>
                      <TableCell className="py-3 text-right">
                        <Text className={`text-sm tabular-nums ${g.urgentes > 0 ? 'text-amber-700 font-semibold' : 'text-zinc-400'}`}>{g.urgentes}</Text>
                      </TableCell>
                      <TableCell className="py-3 text-right">
                        <Text className="text-sm text-zinc-700 tabular-nums">{antiguedad(g.masViejo)}</Text>
                      </TableCell>
                    </TableRow>
                    {open && (
                      <TableRow className="bg-azul-surelia/5">
                        <TableCell colSpan={6} className="py-2 px-6">
                          <div className="max-w-4xl space-y-1">
                            {g.rows.map((p) => {
                              const asignados = normalizarAsignados(p).map((u) => (u === uid ? 'Yo' : (nombreDe(u) || ''))).filter(Boolean);
                              return (
                                <div
                                  key={p.id}
                                  className="w-full flex flex-wrap items-center gap-x-4 gap-y-1 bg-white rounded-lg border border-zinc-200 px-3 py-2"
                                >
                                  <span className="text-sm text-zinc-800 min-w-[200px] flex-1">{p.titulo}</span>
                                  <span className="text-xs text-zinc-500">{PENDIENTE_TIPO_LABELS[p.tipo] || p.tipo}</span>
                                  {groupBy !== 'fiado' && (
                                    esDeLaOrg(p) ? (
                                      <span className="text-xs text-zinc-500">{ORG_LABEL}</span>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() => irAlRelacionado(p)}
                                        className="text-xs text-azul-surelia hover:underline truncate max-w-[180px]"
                                      >
                                        {p.clientName}
                                      </button>
                                    )
                                  )}
                                  {p.afianzadora && groupBy !== 'afianzadora' && (
                                    <span className="text-xs text-zinc-500">{p.afianzadora}</span>
                                  )}
                                  {asignados.length > 0 && groupBy !== 'asignado' && (
                                    <span className="text-xs text-zinc-500">{asignados.join(', ')}</span>
                                  )}
                                  <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${URGENCIA_STYLE[p.urgencia] || URGENCIA_STYLE.normal}`}>
                                    {URGENCIA_LABEL[p.urgencia] || p.urgencia}
                                  </span>
                                  <span className="text-xs text-zinc-500 tabular-nums">{fmtFecha(p.fechaLimite)}</span>
                                </div>
                              );
                            })}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export default ReportePendientes;
